import type { DomSample } from "../ui/dom_backend/dom_animations.js";
import { ITransformParams } from "../types/transform_params.js";
import { Durable } from "./durable.js";

export enum Easing
{
  Linear,
  EaseInPow,
  EaseInQuad,
  EaseOutQuad,
  EaseInOutQuad,
}

export enum TransformType
{
  Moving,
  Scaling,
  Rotating,
  Opacity,
  Counter,
  MovingAndScaling,
  Shine
}

export abstract class TransformBase extends Durable
{
  private readonly type:TransformType;

  private readonly easing: Easing;
  private readonly easing_pow: number;

  private readonly ping_pong_behaviour: boolean;
  private readonly ping_pong_threshold: number;
  private readonly infinite_loop: boolean;
  private readonly early_finish_ratio: number;
  private readonly is_decorative: boolean;

  private readonly is_important: boolean;

  private is_initialized:boolean = false;
  private onFinished?: ( target:any ) => void;

  public dom_animation: Animation | null = null;
  public dom_pending_timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    type:TransformType,
    anim_start_time: number,
    duration_in_ms: number,
    is_important: boolean,
    optional_params: ITransformParams = {}
  )
  {
    super( anim_start_time, duration_in_ms );

    this.type         = type;
    this.is_important = is_important;

    const {
      easing              : easing              = Easing.Linear,
      easing_pow          : easing_pow          = 1,
      ping_pong_behaviour : ping_pong_behaviour = false,
      ping_pong_threshold : ping_pong_threshold = 1.0,
      infinite_loop       : infinite_loop       = false,
      early_finish_ratio  : early_finish_ratio  = 1.0,
      decorative          : decorative          = false
    } = optional_params;

    this.easing              = easing;
    this.easing_pow          = easing_pow;
    this.ping_pong_behaviour = ping_pong_behaviour;
    this.ping_pong_threshold = ping_pong_threshold;
    this.infinite_loop       = infinite_loop;
    this.early_finish_ratio  = early_finish_ratio;
    this.is_decorative       = decorative;

    if ( !is_important )
      this.is_initialized = true;

    //console.log( `TransformBase.constructor()  is_important ${this.is_important}` );

    if ( ping_pong_threshold <= 0.0 || ping_pong_threshold >= 2.0 )
      console.error( `incorrect ping_pong_threshold: ${ping_pong_threshold}` );
  }

  public setOnFinished( onFinished:( target:any ) => void )
  {
    this.onFinished = onFinished;
  }

  public callOnFinished()
  {
    if ( this.onFinished )
      this.onFinished( this.getTarget() );
  }


  public getType():TransformType
  {
    return this.type;
  }

  //public abstract startValue():any;
  //public abstract destValue ():any;

  //public abstract setStart      ( value:any ):void;
  //public abstract setDestination( value:any ):void;

  public isFinished( now:number ): boolean
  {
    return !this.infinite_loop && super.isFinished( now );
  }

  public isImportant(): boolean { return this.is_important; }

  public getEasing(): Easing { return this.easing; }
  public getEasingPow(): number { return this.easing_pow; }
  public isPingPong(): boolean { return this.ping_pong_behaviour; }
  public getPingPongThreshold(): number { return this.ping_pong_threshold; }
  public isInfiniteLoop(): boolean { return this.infinite_loop; }
  public getEarlyFinishRatio(): number { return this.early_finish_ratio; }
  public isDecorative(): boolean { return this.is_decorative; }

  public hasExplicitFrom(): boolean { return false; }

  public domSample( _progress: number ): DomSample | null { return null; }

  public domEasing(): string | null { return null; }

  public sampleProgress( t: number ): number
  {
    let progress = t;
    if ( this.ping_pong_behaviour )
    {
      progress *= 2;
      progress = progress < this.ping_pong_threshold
        ? progress / this.ping_pong_threshold
        : ( 2 - progress ) / ( 2 - this.ping_pong_threshold );
    }
    return TransformBase.applyEasing( this.easing, progress, this.easing_pow );
  }

  public isDomPhaseOver( now: number ): boolean
  {
    return !this.infinite_loop
      && ( this.isFinished( now ) || this.calcProgress( now ) >= this.early_finish_ratio );
  }

  // Advance the animation by dt milliseconds.
  // @param dt Delta time since last update (ms)
  // @returns true if the transform has finished (and is not looping)
  public updateTransform( now: number ): void
  {
    //if ( !this.isStarted( now ) ) // should be already checked
    //  return false;

    if ( !this.is_initialized )
    {
      this.initImportant();
      this.is_initialized = true;
    }

    let progress = this.calcProgress( now );

    if ( this.infinite_loop )
    {
      // wrap progress for looping
      progress = progress % 1;
    } else
    {
      if ( progress > this.early_finish_ratio )
      {
        this.setFinished( now );
        progress = 1;
      }
    }

    if ( this.ping_pong_behaviour )
    {
      progress *= 2;
      if ( progress < this.ping_pong_threshold )
        progress /= this.ping_pong_threshold;
      else
        progress = (2 - progress) / (2 - this.ping_pong_threshold);
    }

    //console.log( `start_time ${this.start_time}   now ${now}   elapsed ${elapsed}  duration ${this.duration}   progress ${progress}   loop ${this.infinite_loop}` );

    // Ping-pong behavior: scale up then back down

    const eased = TransformBase.applyEasing( this.easing, progress, this.easing_pow );
    this.onUpdateTransform( eased );
  }

  protected abstract getTarget():any;

  /** The animated object, for code outside the transform (the DOM backend). */
  public getAnimatedTarget(): unknown
  {
    return this.getTarget();
  }

  protected abstract initImportant():void;

  // Called each update to apply the interpolated value
  // @param progress Normalized progress [0..1]
  protected abstract onUpdateTransform( progress: number ): void;

  //
  public static applyEasing( easing: Easing, t: number, easing_pow:number ): number
  {
    switch ( easing )
    {
      case Easing.EaseInPow:
        return Math.pow( t, easing_pow );

      case Easing.EaseInQuad:
        return t * t;

      case Easing.EaseOutQuad:
        return t * ( 2 - t );

      case Easing.EaseInOutQuad:
        return t < 0.5
          ? 2 * t * t
          : -1 + ( 4 - 2 * t ) * t;

      case Easing.Linear:
      default:
        return t;
    }
  }

  public abstract toStrTransform():string;
}
